<template>
  <div class="foundation">
    <b-container
      class="foundation-container"
      fluid
      style="padding: 0px !important;"
    >
      <b-row class="no-gutters">
        <b-col class="d-flex flex-column" sm="5">
          <div class="foundation-header d-flex p-2">
            <level-dropdown></level-dropdown>

            <div
              class="align-middle d-flex align-items-center foundation-header-title"
            >
              Excercise {{ level.level }}: {{ level.title }}
            </div>
          </div>
          <div class="foundation-content">
            <h2>Introduction to Variables</h2>
            <p>
              In programming, we have a way of storing values so that we can
              reuse them throughout our program or change them, if necessary.
              This concept is known as a <strong>variable</strong>. You can
              imagine variable as a box that is used to store any information.
            </p>
            <p>
              In many programming languages, variable has a
              <strong>data type</strong> and we can only put in information of
              same data type to the variable! Data type is the type of
              information we put into the variable. Some common data types
              including:
            </p>
            <ul>
              <li>Integer (number, eg 1, 2, 3, etc…)</li>
              <li>Float (number with a decimal, eg 1.12, 1.001, 21.3, etc)</li>
              <li>Boolean (TRUE or FALSE)</li>
              <li>String (text, eg “Hello”, “text”, etc..)</li>
            </ul>

            <h3>Variable Naming</h3>
            <p>
              Most programming languages use the
              <strong>Camel Case</strong> convention to name variables. The
              first phrase starts with all lower case while next phrases begins
              with capital letter. Example
            </p>
            <ul>
              <li>myName</li>
              <li>myFavouriteColour</li>
              <li>bigCar</li>
              <li>isGoodStudent</li>
            </ul>

            <h3>Creating Variable</h3>
            <p>
              In blockly you can create an variable like below
            </p>
            <img :src="`${publicPath}img/level_1.png`" />

            <div class="activity-header my-3">
              Activity
            </div>
            <p>
              Create a variable called <span class="variable">myText</span> and
              assign it a String of <span class="string">Hello World!</span>
            </p>
          </div>
        </b-col>
        <b-col>
          <div class="foundation-actionbar d-flex justify-content-end">
            <b-button class="my-auto mr-2" @click="viewCode">
              View Code
            </b-button>
            <b-button
              variant="danger"
              class="my-auto mr-2"
              @click="resetWorkspace"
            >
              Reset
            </b-button>
            <b-button variant="primary" class="my-auto mr-2" @click="runCode">
              <font-awesome-icon
                class="mr-2"
                :icon="['fas', 'play']"
                style="color: white;"
              />Run
            </b-button>
          </div>
          <div id="blocklyDiv"></div>
        </b-col>
      </b-row>
    </b-container>

    <b-modal v-model="showCode" title="JavaScript Code">
      <p class="my-4" style="white-space:pre-wrap;">
        {{ code }}
      </p>
    </b-modal>

    <b-modal
      v-model="showResultModal"
      title="Code result"
      @ok="onClickResultModalOk"
    >
      <div class="text-center">
        <h3>
          {{ isResultCorrect ? "Correct" : "Wrong" }}
        </h3>
        <p class="my-4" style="white-space:pre-wrap;">
          {{ resultMessage }}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import LevelDropdown from "@/components/LevelDropdown.vue";
import Blockly from "blockly";
import Interpreter from "js-interpreter";
import LevelRepository from "@/repositories/level-repository";

import UserServices from "@/services/user-services";
import { mapState } from "vuex";

export default {
  name: "variable-one",

  components: {
    LevelDropdown
  },

  data() {
    return {
      id: this.$route.params.id,
      publicPath: process.env.BASE_URL,
      workspace: null,
      code: "",
      showCode: false,
      showResultModal: false,
      isResultCorrect: false,
      resultMessage: "",
      levels: [],
      level: {
        level: "",
        title: "",
        toolbox: null
      },
      result: {}
    };
  },

  mounted() {
    this.getLevels();
    this.getLevel();
    this.injectBlockly();
  },

  computed: {
    ...mapState(["user"])
  },

  methods: {
    initPage() {
      this.removeBlocklyInjection();
      this.getLevels();
      this.getLevel();
      this.injectBlockly();
    },

    injectBlockly() {
      this.workspace = Blockly.inject(document.getElementById("blocklyDiv"), {
        trashcan: true,
        toolbox: this.level.toolbox
      });

      if (this.level.workspace != undefined) {
        let workspaceBlock = Blockly.Xml.textToDom(this.level.workspace);
        Blockly.Xml.domToWorkspace(workspaceBlock, this.workspace);
      }
    },

    getLevel() {
      this.level = LevelRepository.getLevel(this.id);
    },

    getLevels() {
      this.levels = LevelRepository.getLevels();
    },

    viewCode() {
      this.code = Blockly.JavaScript.workspaceToCode(this.workspace);
      this.showCode = true;
    },

    resetWorkspace() {
      if (confirm("Are you sure you want to reset this question?")) {
        this.result = {};
        document.getElementById("blocklyDiv").innerHTML = "";
        this.injectBlockly();
      }
    },

    removeBlocklyInjection() {
      document.getElementById("blocklyDiv").innerHTML = "";
    },

    runCode() {
      let workspaceCode = Blockly.JavaScript.workspaceToCode(this.workspace);
      let code = this.appendCode(workspaceCode);
      let myInterpreter = new Interpreter(code, this.getInitApi());
      myInterpreter.run();
      this.checkResult();
    },

    appendCode(code) {
      code += "try {returnResult(myText);} catch (err) {};";
      return code;
    },

    getInitApi() {
      const self = this;
      return (interpreter, scope) => {
        const wrapper = function(myText) {
          self.result = {
            myText: myText
          };
        };
        interpreter.setProperty(
          scope,
          "returnResult",
          interpreter.createNativeFunction(wrapper)
        );
      };
    },

    checkResult() {
      const self = this;
      self.isResultCorrect = false;
      if (this.result.myText === undefined) {
        self.resultMessage = "myText variable is not created";
      } else if (this.result.myText.data === "Hello World!") {
        self.resultMessage = "Great! Let move on to the next task.";
        self.isResultCorrect = true;

        if (this.user.uid != "") {
          this.updateUserDoneActivity();
        }
      } else {
        self.resultMessage =
          "myText variable is not assigned to the correct value";
      }
      self.showResultModal = true;
    },

    onClickResultModalOk() {
      if (this.isResultCorrect) {
        let nextLevel = parseInt(this.id) + 1;
        this.$router.push("/foundation/" + nextLevel);
      } else {
        this.showResultModal = false;
      }
    },

    async updateUserDoneActivity() {
      await UserServices.updateUserDoneActivity(
        this.user.uid,
        this.level.firebaseId,
        "foundation"
      );
    }
  }
};
</script>

<style lang="less" scoped>
.foundation {
  &-header {
    background-color: rgb(255, 223, 137);

    &-title {
      line-height: 1.25rem;
      font-size: 1.25rem;
    }
  }

  &-content {
    padding: 15px;
    overflow: auto;
    height: calc(100vh - 120px);
  }

  &-actionbar {
    height: 54px;
  }
}

#blocklyDiv {
  height: calc(100vh - 110px);
  width: 100%;
}
</style>
